import React, { useEffect, useState } from "react";
import {
	IconButton,
	Tooltip,
	Typography,
	Link,
	Button,
	Grid,
	LinearProgress,
	Box,
} from "@mui/material";
import { useSelector } from "react-redux";

import { CommentIcon } from "utility/icons";
import "../WorkFlowApprovalStepsHistory/WorkFlowApprovalStepsHistory.css";
import { Shared } from "utility/shared";
import Emitter from "utility/emitter";
import { RequisitionStepHistoryStatus } from "utility/enums/requisition-entry";
import ResponsiveDialog from "components/ResponsiveDialog";
import { RootState } from "redux/store";
import { IStepHistoryComment } from "models/StepHistoryDetail";
import { CustomTooltip } from "components/CustomTooltip";
import {
	IS_ITEM_ASSIGNED_TO_USER,
	OPEN_ITEM_STEP_HISTORY_DETAIL,
} from "utility/emitterEvents";
import { ItemStepHistoryEmitter } from "models/Item/itemStepHistoryEmitter";
import { IItemStepHistory } from "models/Item/itemStepHistory";
import { ItemService } from "services/item.service";
import { Loading } from "components/Loading";

type Props = {
	itemDetail: ItemStepHistoryEmitter;
};

const ItemWorkFlowApprovalStepsHistory: React.FC<Props> = ({ itemDetail }) => {
	const userDetails = useSelector(
		(state: RootState) => state.authState.userDetails
	);
	const [itemStepHistoryList, setItemStepHistoryList] = useState<
		IItemStepHistory[]
	>([]);
	const [openInfoDialog, setOpenInfoDialog] = useState<{
		message: string;
		open: boolean;
	}>({
		message: "",
		open: false,
	});
	const [activeStepIndex, setActiveStepIndex] = useState<number>(-1);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const isItemStepAssignedToUser = (step: IItemStepHistory) => {
		return step.assignToUsers && step.assignToUsers.length > 0
			? step.assignToUsers.findIndex((r) => r.userId === userDetails.userId) >
					-1
			: false;
	};

	const openItemStepHistoryDetail = (item: IItemStepHistory) => {
		Emitter.off(OPEN_ITEM_STEP_HISTORY_DETAIL, {} as any);
		Emitter.emit(OPEN_ITEM_STEP_HISTORY_DETAIL, item);
	};

	const setPrevStepAsSelected = (
		stepHistoryList: IItemStepHistory[],
		filteredList: IItemStepHistory[]
	) => {
		for (let i = 0; i < filteredList.length; i++) {
			const element = filteredList[i];
			if (isItemStepAssignedToUser(element)) {
				const index = stepHistoryList.findIndex(
					(r) => r.limitedItemStepId === element.limitedItemStepId
				);
				if (index > -1) {
					setActiveStepIndex(index);
					openItemStepHistoryDetail(stepHistoryList[index]);
					Emitter.emit(
						IS_ITEM_ASSIGNED_TO_USER,
						isItemStepAssignedToUser(stepHistoryList[index])
					);
					break;
				}
			}
			// if no prev step is assigned to user, set first step as selected
			if (filteredList.length - 1 === i) {
				setActiveStepIndex(0);
				openItemStepHistoryDetail(stepHistoryList[0]);
			}
		}
	};

	useEffect(() => {
		// get RequisitionStepHistoryList
		if (itemDetail.itemId > -1) {
			setIsLoading(true);
			setActiveStepIndex(-1);
			ItemService.getItemStepHistoryList(itemDetail.itemId)
				.then((result) => {
					setItemStepHistoryList(result);
					const index = result.findIndex(
						(r) => r.limitedItemStepId === itemDetail.limitedItemStepId
					);
					if (index > -1) {
						if (isItemStepAssignedToUser(result[index])) {
							setActiveStepIndex(index);
							Emitter.emit(
								IS_ITEM_ASSIGNED_TO_USER,
								isItemStepAssignedToUser(result[index])
							);
						} else {
							setPrevStepAsSelected(
								result,
								JSON.parse(JSON.stringify(result)).slice(0, index).reverse()
							);
						}
					} else {
						setPrevStepAsSelected(
							result,
							JSON.parse(JSON.stringify(result)).reverse()
						);
					}
					setIsLoading(false);
				})
				.catch((err) => {
					Shared.showSnackbarMessage(
						"Error loading limited item step history list, please try again"
					);
					setIsLoading(false);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [itemDetail.itemId, itemDetail.limitedItemStepId]);

	const getClassName = (status: string) => {
		let classes: string = "steps-root ";
		if (status === RequisitionStepHistoryStatus.Ready) {
			classes += "in-progress";
		}
		if (status === RequisitionStepHistoryStatus.Completed) {
			classes += "complete";
		}
		if (status === RequisitionStepHistoryStatus.Recalled) {
			classes += "recall";
		}
		if (status === RequisitionStepHistoryStatus.ChangeRequested) {
			classes += "change-request";
		}
		if (status === RequisitionStepHistoryStatus.Rejected) {
			classes += "reject";
		}
		if (status === RequisitionStepHistoryStatus.Deleted) {
			classes += "delete";
		}
		if (status === RequisitionStepHistoryStatus.Delegated) {
			classes += "delegate";
		}
		if (status === RequisitionStepHistoryStatus.Escalated) {
			classes += "escalate";
		}
		if (status === RequisitionStepHistoryStatus.Cancelled) {
			classes += "cancel";
		}
		return classes;
	};

	const handleStepDetailClick = (item: IItemStepHistory, index: number) => {
		if (activeStepIndex === index) {
			return;
		}
		if (
			item.limitedItemStepStatusName === RequisitionStepHistoryStatus.Pending ||
			(item.limitedItemStepStatusName === RequisitionStepHistoryStatus.Ready &&
				!isItemStepAssignedToUser(item))
		) {
			setOpenInfoDialog({
				open: true,
				message: `The step selected ${item.stepNumber}, has not been completed and there is no information to display for the step.`,
			});
		} else {
			setActiveStepIndex(index);
			openItemStepHistoryDetail(item);
			Emitter.emit(IS_ITEM_ASSIGNED_TO_USER, isItemStepAssignedToUser(item));
		}
	};

	return (
		<div className="history-panel">
			<div className="heading-part">
				<Typography variant="h4">Workflow approval Steps - History</Typography>
				{itemStepHistoryList.length > 0 && (
					<div className="stepper-wrapper">
						<Typography variant="caption">
							{`${itemStepHistoryList[0].stepCompletedInPercentage}% Completed`}
						</Typography>
						<Box sx={{ ml: 1, maxWidth: 400, flexGrow: 1 }}>
							<LinearProgress
								variant="determinate"
								value={itemStepHistoryList[0].stepCompletedInPercentage}
							/>
						</Box>
					</div>
				)}
			</div>
			<div className="steps-wrapper">
				{!itemStepHistoryList.length &&
					(isLoading ? (
						<Loading isWorkflowStepLoader={true} />
					) : (
						<Typography
							className="steps-root"
							variant="body1"
							justifyContent="center"
						>
							No Data Available
						</Typography>
					))}
				{itemStepHistoryList.length > 0 &&
					itemStepHistoryList.map((item: IItemStepHistory, index: number) => (
						<Link
							key={index}
							onClick={(e) => {
								e.preventDefault();
								handleStepDetailClick(item, index);
							}}
							underline="none"
							className={`${getClassName(item.limitedItemStepStatusName)} ${
								index === activeStepIndex && "active"
							}`}
							style={{
								cursor: "pointer",
							}}
						>
							<Tooltip
								title={item.limitedItemStepStatusName}
								placement="bottom"
							>
								<div className="requisition-steps-icon">
									{item.stepNumber.includes(".0")
										? item.stepNumber.split(".")[0]
										: item.stepNumber}
								</div>
							</Tooltip>
							<div className="step-content">
								<Typography variant="body2">{item.description}</Typography>

								<>
									<Typography variant="caption">
										{item.assignToUsers.map((user, index) => (
											<span
												key={index}
												className={
													user.isTaskCompleted ? "green-text fw-500" : ""
												}
											>{`${user.fullName}${
												index !== item.assignToUsers.length - 1 ? ", " : ""
											}`}</span>
										))}
									</Typography>
									<div className="step-content-middle">
										<div className="left-part">
											<Typography variant="caption" className="light">
												Duration
											</Typography>
											<Typography variant="caption">
												{item.duration || "-"}
											</Typography>
										</div>
										<div className="right-part">
											<Typography variant="caption" className="light">
												Avg. Duration
											</Typography>
											<Typography variant="caption">
												{item.averageDuration || "-"}
											</Typography>
										</div>
									</div>
								</>
								<div className="step-content-bottom">
									<Tooltip
										title={
											item.completedDate
												? `Completed by ${item.completedBy}`
												: ""
										}
										arrow
										placement="top"
									>
										<div className="left-part">
											<Typography variant="caption" className="light">
												Date Completed
											</Typography>
											<Typography variant="caption">
												{item.completedDate
													? `${Shared.getFormattedDate(
															new Date(item.completedDate),
															"dd/MM/yyyy"
													  )} - ${Shared.getFormattedDate(
															new Date(item.completedDate),
															"hh:mmaaa"
													  )}`
													: "-"}
											</Typography>
										</div>
									</Tooltip>
									{item.comments.length > 0 && (
										<CustomTooltip
											tooltipStyle={{
												border: "1px solid #034f8b",
											}}
											tooltipProps={{
												title: item.comments.map(
													(cmt: IStepHistoryComment, index: number) => (
														<Grid
															container
															key={index}
															className={index !== 0 ? "history-comment" : ""}
														>
															<Grid item xs={12} className="fw-500">
																{cmt.fullName}
															</Grid>
															<Grid item xs={12} mt={1}>
																{cmt.comment}
															</Grid>
														</Grid>
													)
												),
												className: "history-tooltip",
												placement: "right",
												children: (
													<IconButton>
														<CommentIcon />
													</IconButton>
												),
											}}
										/>
									)}
								</div>
							</div>
						</Link>
					))}
			</div>
			{openInfoDialog.open && (
				<ResponsiveDialog
					open={openInfoDialog.open}
					title="Step History Info"
					showActionButtons={false}
					titleCloseIcon={false}
					maxWidth="xs"
					onCancel={() => {}}
				>
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<Typography variant="h4" color="black">
								{openInfoDialog.message}
							</Typography>
						</Grid>
						<Grid
							item
							xs={12}
							textAlign="center"
							sx={{ paddingTop: "24px !important" }}
						>
							<Button
								variant="contained"
								onClick={() => {
									setOpenInfoDialog({ open: false, message: "" });
								}}
							>
								Close
							</Button>
						</Grid>
					</Grid>
				</ResponsiveDialog>
			)}
		</div>
	);
};

export default ItemWorkFlowApprovalStepsHistory;
